'use client';

import { IntegerRange } from '@monoimin/sb-types';
import { useState, useEffect, useCallback } from 'react';

import { useDebouncedCallback } from '../useDebouncedCallback/useDebouncedCallback';

type PercentageStart = `${IntegerRange<1, 100>}%`;

export type UseScrollProgressArgs = {
  start: number | PercentageStart;
  freeze?: boolean;
  debounceDelay?: number;
};

function isValidNumber(value: unknown) {
  return typeof value === 'number' && !Number.isNaN(value);
}

const getWindowScrollPercentage = () => {
  return (window.scrollY / document.body.scrollHeight) * 100;
};

const formatStartPercentage = (start: PercentageStart) => {
  const formatted = Number(start.replace('%', ''));

  return isValidNumber(formatted) ? formatted : 0;
};

export const useScrollProgress = ({
  start,
  freeze,
  debounceDelay = 50,
}: UseScrollProgressArgs) => {
  const [isScrolled, setScrolled] = useState(false);

  // Use useCallback to stabilize this function reference
  const checkScrollPosition = useCallback(() => {
    if (freeze && isScrolled) {
      return;
    }

    const currentScroll =
      typeof start === 'number'
        ? window.scrollY > start
        : getWindowScrollPercentage() >= formatStartPercentage(start);

    // Only update state if the value actually changed
    if (currentScroll !== isScrolled) {
      setScrolled(currentScroll);
    }
  }, [freeze, isScrolled, start]);

  const handleScroll = useDebouncedCallback(checkScrollPosition, debounceDelay);

  useEffect(() => {
    // Check initial scroll position
    checkScrollPosition();

    // Use passive option for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, checkScrollPosition]);

  return isScrolled;
};
